import React from 'react';
import { observer } from 'mobx-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import cn from 'classnames';
import { When } from 'react-if';
import styled from 'styled-components';
import { message, Popconfirm, Popover } from 'antd';

import { useStores } from '@/hooks/use-stores';
import { permissions } from '@/constants';
import { Device } from '@/store/Station/constants';
import EPCheckbox from '@/comp/EPCheckbox';
import { EPButton } from '@/components';

import IconCellular from '@/assets/icon-cellular.svg?react';
import IconWorld from '@/assets/icon-world.svg?react';
import IconChevronUp from '@/assets/icon-chevron-up.svg?react';
import IconChevronDown from '@/assets/icon-chevron-down.svg?react';
import IconConfigure from '@/assets/icon-configure.svg?react';
import { AlertLevels, AlertStatus } from '@/constants/enums';

import ArchiveIcon from '../common/icons/ArchiveIcon';
import UnarchiveIcon from '../common/icons/UnarchiveIcon';
import SeverityIcon from '../common/icons/SeverityIcon';

export interface AlertEntity {
  id: string;
  alertRuleId: string;
  alertType: string;
  severity: AlertLevels;
  title: string;
  detailedDescription: string;
  openingTime: string;
  recommendation: string;
  status: string;
  phoneNumber: string;
  manufacturerCode: string;
  websiteUrl: string;
  inMaintenance: boolean;
  affectedDevicesWithAttributes: Device[];
  acknowledged: boolean;
  isSelected: boolean;
}

interface AlertProps {
  alert: AlertEntity;
  toggleOpen: (alert: AlertEntity) => void;
  setOpenAlertId: (id: string) => void;
  isOpen: boolean;
  isListWithSelection?: boolean;
}

const Alert = observer(({ alert, toggleOpen, setOpenAlertId, isOpen, isListWithSelection }: AlertProps) => {
  const params = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    id,
    alertRuleId,
    alertType,
    severity,
    title,
    detailedDescription,
    openingTime,
    recommendation,
    status,
    phoneNumber,
    manufacturerCode,
    websiteUrl,
    inMaintenance,
    affectedDevicesWithAttributes,
    acknowledged,
  } = alert ?? {};
  const { alertStore, permissionStore } = useStores();
  const date = moment(openingTime).calendar();

  const onClickArchive = async () => {
    await alertStore.archive(id);
    setOpenAlertId(undefined);
  };

  const onClickUnArchive = async () => {
    try {
      await alertStore.unArchive(id);
      setOpenAlertId(undefined);
    } catch {
      message.error(t('Unarchive alert failed'));
    }
  };

  const onClickRuleIcon = () => {
    navigate(`/home/station/${params.stationId}/rules/${alertType}/${alertRuleId}/${id}/view`);
  };

  const selectClick = () => {
    alert.isSelected = !alert.isSelected;
    if (alert.isSelected) {
      alertStore.addSelectedAlert(alert);
    } else {
      alertStore.removeSelectedAlert(alert);
    }
  };

  return (
    <AlertContainer data-alertid={id} $isInMaintenance={inMaintenance} $isOpen={status === AlertStatus.Open}>
      {isListWithSelection && <EPCheckbox checked={alert.isSelected} onChange={selectClick} />}
      <div className="alert-content">
        {inMaintenance && (
          <Popover
            placement="bottom"
            content={<span>{t('This alert record was triggered while the compressor was In-Maintenance')}</span>}
          >
            <div className="in-maintenance">{t('Received during maintenance')}</div>
          </Popover>
        )}
        <header>
          <SeverityIcon severity={severity} width={24} height={24} />
          <div className="text-container">
            <div className="severity">
              <span className="severity-label">{t(severity)}</span>
              {status === AlertStatus.Close && <span className="expired-label">{t('Expired')}</span>}
            </div>
            <div className="date">{date}</div>
          </div>
        </header>

        <main>
          <h1 className={cn({ ellipsis: !isOpen })}> {title}</h1>
          {affectedDevicesWithAttributes && (
            <p className={cn({ ellipsis: !isOpen })}>
              {affectedDevicesWithAttributes.map((affectedDevice, index) => (
                <span key={index}>{affectedDevice.name} </span>
              ))}
            </p>
          )}
          <p className={cn({ ellipsis: !isOpen })}>{detailedDescription}</p>
          {isOpen ? (
            <IconChevronUp className="anticon-caret" onClick={() => toggleOpen(alert)} />
          ) : (
            <IconChevronDown className="anticon-caret" onClick={() => toggleOpen(alert)} />
          )}
        </main>

        {isOpen && (
          <footer>
            <header>
              <h1>{manufacturerCode}</h1>
              <div className="action-links">
                {permissionStore.isAuth(permissions.UPDATE_ALERT_RECORD) &&
                  (acknowledged === false && !inMaintenance ? (
                    <Popover placement="bottom" content={<span>{t('Archive Alert')}</span>}>
                      <EPButton
                        type="link"
                        size="small"
                        className="icon"
                        icon={<ArchiveIcon />}
                        onClick={onClickArchive}
                      />
                    </Popover>
                  ) : (
                    acknowledged === true &&
                    !inMaintenance && (
                      <Popover placement="bottom" content={<span>{t('Unarchive Alert')}</span>}>
                        <Popconfirm
                          placement="bottom"
                          title={t('Are you sure you want to unarchive this alert record?')}
                          onConfirm={onClickUnArchive}
                          okText={t('Yes')}
                          cancelText={t('cancel')}
                          icon={null}
                        >
                          <EPButton type="link" size="small" className="icon" icon={<UnarchiveIcon />} />
                        </Popconfirm>
                      </Popover>
                    )
                  ))}

                <Popover placement="bottom" content={<span>{t(`View Alert record's rule (logic)`)}</span>}>
                  <EPButton
                    type="link"
                    size="small"
                    icon={<IconConfigure className="icon icon-configure-specific-rule" />}
                    disabled={false}
                    onClick={onClickRuleIcon}
                  />
                </Popover>
              </div>
            </header>
            <main>
              <When condition={!!recommendation}>
                <div className="recommendation-container">
                  <h1>{t('RECOMMENDATION')}</h1>
                  <p>{recommendation}</p>
                </div>
              </When>

              <When condition={!!phoneNumber && !!websiteUrl}>
                <div className="contact-us-container">
                  <h1>{t('Or contact us via:')}</h1>
                  <ul>
                    <When condition={!!phoneNumber}>
                      <li>
                        <IconCellular className="icon" />
                        <div className="label phone-number">{phoneNumber}</div>
                      </li>
                    </When>

                    <When condition={!!websiteUrl}>
                      <li>
                        <IconWorld className="icon" />
                        <div className="label url">
                          <a href={websiteUrl} target="_blank" rel="noreferrer">
                            {websiteUrl}
                          </a>
                        </div>
                      </li>
                    </When>
                  </ul>
                </div>
              </When>
            </main>
          </footer>
        )}
      </div>
    </AlertContainer>
  );
});

interface AlertContainerProps {
  $isInMaintenance: boolean;
  $isOpen: boolean;
}

const AlertContainer = styled.div<AlertContainerProps>`
  margin-top: ${({ theme: { token } }) => token.margin}px;
  border-radius: 2px;
  border: ${({ $isInMaintenance, theme: { token } }) =>
    $isInMaintenance ? `2px solid ${token.Ecoplant_Compressor.colorBGMaintenance}` : 'unset'};
  display: flex;
  .alert-content {
    background-color: ${({ theme: { token } }) => token.colorBgElevated};
    width: calc(100% - 25px);
    margin-left: ${({ theme: { token } }) => token.marginXS}px;
    .in-maintenance {
      background-color: ${({ theme: { token } }) => token.Ecoplant_Compressor.colorBGMaintenance};
      font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
      font-weight: normal;
      color: ${({ theme: { token } }) => token.colorWarningTextHover};
      padding-left: ${({ theme: { token } }) => token.paddingSM}px;
    }

    > header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      background-color: ${({ theme: { token } }) => token.colorBorderSecondary};
      border-radius: ${({ $isInMaintenance }) => ($isInMaintenance ? 'unset' : '5px')};

      .anticon {
        font-size: ${({ theme: { token } }) => token.fontSizeHeading3}px;
        opacity: ${({ $isOpen }) => ($isOpen === true ? 1 : 0.5)};
      }

      .text-container {
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-grow: 1;
        padding: 0 ${({ theme: { token } }) => token.paddingSM}px;

        .severity {
          font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
          text-transform: capitalize;

          .severity-label {
            opacity: ${({ $isOpen }) => ($isOpen === true ? 1 : 0.5)};
          }
          .expired-label {
            margin-left: ${({ theme: { token } }) => token.marginSM}px;
            padding: 0 ${({ theme: { token } }) => token.paddingXXS}px;
            border: 1px solid ${({ theme: { token } }) => token.colorText};
          }
        }

        .date {
          font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
          opacity: ${({ $isOpen }) => ($isOpen === true ? 1 : 0.5)};
        }
      }
    }

    > main {
      position: relative;
      padding: ${({ theme: { token } }) =>
        `${token.paddingSM}px ${token.paddingLG}px ${token.paddingSM}px ${token.paddingSM}px`};
      opacity: ${({ $isOpen }) => ($isOpen === true ? 1 : 0.5)};

      h1 {
        font-size: ${({ theme: { token } }) => token.fontSize}px;
      }

      p {
        margin-top: ${({ theme: { token } }) => token.marginXXS}px;
        font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
        opacity: 0.65;
      }
    }

    > footer {
      padding: ${({ theme: { token } }) => token.paddingSM}px;
      background-color: ${({ theme: { token } }) => token.colorBorderSecondary};
      border-radius: 0px 0px 2px 2px;

      header {
        display: flex;
        align-items: center;

        h1 {
          font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
          color: ${({ theme: { token } }) => token.colorPrimary};
          flex-grow: 1;
        }

        .action-links {
          display: flex;
          align-items: center;

          button {
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .icon {
            cursor: pointer;
            width: 16px;
            height: 16px;
          }

          .icon-configure-specific-rule {
            path {
              fill: ${({ theme: { token } }) => token.colorText};
            }
          }
        }
      }

      main {
        .recommendation-container {
          margin-top: ${({ theme: { token } }) => token.marginSM}px;

          h1 {
            font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
            color: ${({ theme: { token } }) => token.colorBorder};
          }

          p {
            margin-top: ${({ theme: { token } }) => token.marginXXS}px;
            font-size: ${({ theme: { token } }) => token.fontSize}px;
            color: ${({ theme: { token } }) => token.colorBgContainer};
            font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
          }
        }

        .contact-us-container {
          margin-top: ${({ theme: { token } }) => token.marginSM}px;

          h1 {
            font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
            color: ${({ theme: { token } }) => token.colorBorder};
          }

          ul {
            margin-top: ${({ theme: { token } }) => token.marginXXS}px;
            color: ${({ theme: { token } }) => token.colorBgContainer};

            li {
              display: flex;
              align-items: center;
              padding-top: ${({ theme: { token } }) => token.paddingXXS}px;

              .icon {
              }

              .label {
                padding-left: ${({ theme: { token } }) => token.paddingXXS}px;

                a {
                  color: ${({ theme: { token } }) => token.colorPrimary};
                }
              }
            }
          }
        }
      }
    }

    .anticon-caret {
      position: absolute;
      right: 12px;
      bottom: 10px;
      cursor: pointer;
    }

    &:first-of-type {
      margin-top: 0;
    }
  }
`;

export default Alert;
